import axios from "../../axiosConfig";
import {
  fetchOrganizationSurveysPayload,
  normalizeOrganizationSurveys,
} from "./organizationSurveysApi";

const WORKBOOK_SURVEY_ID_KEY = "organization_survey_id";
const SCOPED_WORKBOOK_SURVEY_ID_KEY = "workbook_organization_survey_id";

export class NoWorkbookDataError extends Error {
  constructor(message = "No workbook data available yet. Run a survey to generate insights.") {
    super(message);
    this.name = "NoWorkbookDataError";
  }
}

export const isNoWorkbookDataError = (error) =>
  error instanceof NoWorkbookDataError || error?.name === "NoWorkbookDataError";

const normalizeOrganizationSurveyId = (organizationSurveyId) => {
  if (organizationSurveyId == null || organizationSurveyId === "") {
    return null;
  }

  return Number(organizationSurveyId);
};

const getSurveyId = (survey) => normalizeOrganizationSurveyId(
  survey?.organization_survey_id ?? survey?.id
);

const getSurveyOrganizationId = (survey) => normalizeOrganizationSurveyId(
  survey?.organization_id ?? survey?.organization?.id
);

const getWorkbookUsableSurveyIds = (surveysPayload) =>
  normalizeOrganizationSurveys(surveysPayload)
    .filter((survey) => survey?.workbook_usable === true)
    .map(getSurveyId)
    .filter(Boolean);

const buildWorkbookStoragePayload = (organizationSurveyId, surveysPayload = null) => {
  const normalizedSurveyId = normalizeOrganizationSurveyId(organizationSurveyId);
  const surveys = normalizeOrganizationSurveys(surveysPayload);
  const matchingSurvey = surveys.find(
    (survey) => String(getSurveyId(survey)) === String(normalizedSurveyId)
  );

  return {
    organizationSurveyId: normalizedSurveyId,
    organizationId: getSurveyOrganizationId(matchingSurvey),
  };
};

const readStoredWorkbookOrganizationSurveyId = (surveysPayload) => {
  if (typeof sessionStorage === "undefined") return null;

  const usableSurveyIds = getWorkbookUsableSurveyIds(surveysPayload);
  const isStillUsable = (organizationSurveyId) =>
    usableSurveyIds.some((surveyId) => String(surveyId) === String(organizationSurveyId));

  const storedPayload = sessionStorage.getItem(SCOPED_WORKBOOK_SURVEY_ID_KEY);
  if (storedPayload) {
    try {
      const parsedPayload = JSON.parse(storedPayload);
      const scopedSurveyId = normalizeOrganizationSurveyId(
        parsedPayload?.organizationSurveyId
      );

      if (scopedSurveyId && isStillUsable(scopedSurveyId)) {
        return scopedSurveyId;
      }
    } catch (error) {
      sessionStorage.removeItem(SCOPED_WORKBOOK_SURVEY_ID_KEY);
    }
  }

  const legacySurveyId = normalizeOrganizationSurveyId(
    sessionStorage.getItem(WORKBOOK_SURVEY_ID_KEY)
  );

  return legacySurveyId && isStillUsable(legacySurveyId) ? legacySurveyId : null;
};

export const clearWorkbookOrganizationSurveyId = () => {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(SCOPED_WORKBOOK_SURVEY_ID_KEY);
  sessionStorage.removeItem(WORKBOOK_SURVEY_ID_KEY);
};

export const persistWorkbookOrganizationSurveyId = (
  organizationSurveyId,
  surveysPayload = null
) => {
  if (typeof sessionStorage === "undefined") return;
  const normalizedSurveyId = normalizeOrganizationSurveyId(organizationSurveyId);
  if (!normalizedSurveyId) return;

  const scopedPayload = buildWorkbookStoragePayload(normalizedSurveyId, surveysPayload);
  sessionStorage.setItem(SCOPED_WORKBOOK_SURVEY_ID_KEY, JSON.stringify(scopedPayload));
  sessionStorage.setItem(WORKBOOK_SURVEY_ID_KEY, String(organizationSurveyId));
};

export const resolveWorkbookOrganizationSurveyId = async ({
  routeStateOrganizationSurveyId = null,
  organizationSurveyId = null,
  organizationId = null,
  surveysPayload = null,
} = {}) => {
  const routeStateSurveyId = normalizeOrganizationSurveyId(
    routeStateOrganizationSurveyId
  );
  if (routeStateSurveyId) {
    persistWorkbookOrganizationSurveyId(routeStateSurveyId);
    return routeStateSurveyId;
  }

  const preferredSurveyId = normalizeOrganizationSurveyId(organizationSurveyId);
  if (preferredSurveyId) {
    persistWorkbookOrganizationSurveyId(preferredSurveyId);
    return preferredSurveyId;
  }

  const resolvedSurveysPayload =
    surveysPayload ?? await fetchOrganizationSurveysPayload({ organizationId });
  const resolvedSurveyId = normalizeOrganizationSurveyId(
    resolvedSurveysPayload?.recommended_workbook_organization_survey_id
  );

  if (resolvedSurveyId) {
    persistWorkbookOrganizationSurveyId(resolvedSurveyId, resolvedSurveysPayload);
    return resolvedSurveyId;
  }

  const storedSurveyId = readStoredWorkbookOrganizationSurveyId(resolvedSurveysPayload);
  if (storedSurveyId) {
    return storedSurveyId;
  }

  throw new NoWorkbookDataError();
};

const buildWorkbookBasePath = (organizationSurveyId) =>
  `/organization_surveys/${organizationSurveyId}/workbook`;

export const getWorkbookSummary = async (organizationSurveyId) => {
  const fullUrl = `${buildWorkbookBasePath(organizationSurveyId)}/summary`;
  const response = await axios.get(fullUrl);
  return response.data;
};

export const getWorkbookOverview = async (organizationSurveyId, kind) => {
  const fullUrl = `${buildWorkbookBasePath(organizationSurveyId)}/${kind}`;
  const response = await axios.get(fullUrl);
  return response.data;
};

export const getWorkbookPageInterpretation = async (
  organizationSurveyId,
  pageId
) => {
  const fullUrl = `${buildWorkbookBasePath(organizationSurveyId)}/pages/${pageId}/interpretation`;
  const response = await axios.get(fullUrl);
  return response.data;
};

export const getWorkbookWidgets = async (
  organizationSurveyId,
  interpretationId
) => {
  const fullUrl = `${buildWorkbookBasePath(organizationSurveyId)}/interpretations/${interpretationId}/widgets`;
  const response = await axios.get(fullUrl);
  return response.data;
};
